import React, { useState } from 'react';
import { Brain, Skull, Sparkles, Radio, AlertTriangle } from 'lucide-react';

function FarOutDiscoveries({ farOut }) {
  const [expandedTier, setExpandedTier] = useState('all');

  if (!farOut) {
    return (
      <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6">
        <p className="text-gray-400">No far-out analysis results available</p>
      </div>
    );
  }

  // Categorize discoveries
  const discoveries = [];
  
  const checkForDiscovery = (result, tier) => {
    if (result && result.interpretation && 
        (result.interpretation.includes('⚠️') || 
         result.interpretation.includes('DETECTED'))) {
      discoveries.push({
        ...result,
        tier: tier
      });
    }
  };

  // Scan all tiers
  if (farOut.tier1_plausible) {
    Object.values(farOut.tier1_plausible).forEach(r => checkForDiscovery(r, 'tier1_plausible'));
  }
  if (farOut.tier2_speculative) {
    Object.values(farOut.tier2_speculative).forEach(r => checkForDiscovery(r, 'tier2_speculative'));
  }
  if (farOut.tier3_wild) {
    Object.values(farOut.tier3_wild).forEach(r => checkForDiscovery(r, 'tier3_wild'));
  }
  if (farOut.tier4_bonkers) {
    Object.values(farOut.tier4_bonkers).forEach(r => checkForDiscovery(r, 'tier4_bonkers'));
  }

  const getTierIcon = (tier) => {
    switch(tier) {
      case 'tier1_plausible': return <Sparkles className="w-5 h-5 text-blue-400" />;
      case 'tier2_speculative': return <Radio className="w-5 h-5 text-purple-400" />;
      case 'tier3_wild': return <Brain className="w-5 h-5 text-pink-400" />;
      case 'tier4_bonkers': return <Skull className="w-5 h-5 text-red-400" />;
      default: return null;
    }
  };

  const getTierColor = (tier) => {
    switch(tier) {
      case 'tier1_plausible': return 'from-blue-900/50 to-blue-800/30 border-blue-500/30';
      case 'tier2_speculative': return 'from-purple-900/50 to-purple-800/30 border-purple-500/30';
      case 'tier3_wild': return 'from-pink-900/50 to-pink-800/30 border-pink-500/30';
      case 'tier4_bonkers': return 'from-red-900/50 to-red-800/30 border-red-500/30';
      default: return '';
    }
  };

  const getTierLabel = (tier) => {
    switch(tier) {
      case 'tier1_plausible': return 'Tier 1: Plausible Physics';
      case 'tier2_speculative': return 'Tier 2: Speculative';
      case 'tier3_wild': return 'Tier 3: Wild Speculation';
      case 'tier4_bonkers': return 'Tier 4: Completely Bonkers';
      default: return tier;
    }
  };

  const renderAnalysisCard = (name, result, tier) => (
    <div 
      key={name}
      className={`bg-gradient-to-br ${getTierColor(tier)} rounded-lg p-4 border`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-1">
          {getTierIcon(tier)}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold mb-1">{result.name || name}</h4>
          <p className="text-sm text-gray-300 mb-2">{result.interpretation}</p>
          
          {result.confidence && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-400">Confidence:</span>
              <span className={`font-semibold ${
                result.confidence === 'HIGH' ? 'text-red-400' :
                result.confidence === 'MEDIUM' ? 'text-yellow-400' :
                result.confidence === 'LOW' ? 'text-blue-400' :
                'text-gray-400'
              }`}>
                {result.confidence}
              </span>
            </div>
          )}

          {result.implication && (
            <p className="text-xs text-gray-400 mt-2 italic">
              → {result.implication}
            </p>
          )}

          {tier === 'tier4_bonkers' && result.warning && (
            <div className="mt-3 bg-red-900/30 border border-red-500/30 rounded p-2">
              <p className="text-xs text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {result.warning}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-800/50 backdrop-blur rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold flex items-center gap-2">
          <Brain className="w-6 h-6 text-pink-400" />
          Far-Out Phenomena Detection
        </h3>
        <div className="text-sm text-gray-400">
          12 analyses | {discoveries.length} discoveries
        </div>
      </div>

      {/* Discovery Summary */}
      {discoveries.length > 0 ? (
        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h4 className="font-bold text-red-400">ANOMALIES DETECTED</h4>
          </div>
          <p className="text-sm text-gray-300 mb-3">
            Found {discoveries.length} potentially paradigm-shifting phenomena:
          </p>
          <ul className="space-y-1 text-sm">
            {discoveries.map((d, idx) => (
              <li key={idx} className="flex items-center gap-2">
                {getTierIcon(d.tier)}
                <span className="font-semibold">{d.name}</span>
                <span className="text-gray-400">- {getTierLabel(d.tier)}</span>
              </li>
            ))}
          </ul>
          {discoveries.some(d => d.tier === 'tier4_bonkers') && (
            <div className="mt-3 pt-3 border-t border-red-500/30">
              <p className="text-xs text-red-300 font-semibold">
                ⚠️ Tier 4 findings detected. Triple-check for bugs before calling press conference.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-green-400" />
            <p className="text-sm text-gray-300">
              No anomalous phenomena detected. Universe appears to follow standard quantum mechanics.
            </p>
          </div>
        </div>
      )}

      {/* Tier Selector */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        <button
          onClick={() => setExpandedTier('all')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
            expandedTier === 'all' 
              ? 'bg-purple-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          All (12)
        </button>
        <button
          onClick={() => setExpandedTier('tier1_plausible')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
            expandedTier === 'tier1_plausible' 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Tier 1: Plausible (3)
        </button>
        <button
          onClick={() => setExpandedTier('tier2_speculative')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
            expandedTier === 'tier2_speculative' 
              ? 'bg-purple-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Tier 2: Speculative (3)
        </button>
        <button
          onClick={() => setExpandedTier('tier3_wild')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
            expandedTier === 'tier3_wild' 
              ? 'bg-pink-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Tier 3: Wild (3)
        </button>
        <button
          onClick={() => setExpandedTier('tier4_bonkers')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
            expandedTier === 'tier4_bonkers' 
              ? 'bg-red-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Tier 4: Bonkers (3)
        </button>
      </div>

      {/* Tier 1: Plausible */}
      {(expandedTier === 'all' || expandedTier === 'tier1_plausible') && farOut.tier1_plausible && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-400" />
            Tier 1: Plausible Physics
          </h4>
          <div className="grid gap-3">
            {Object.entries(farOut.tier1_plausible).map(([name, result]) => 
              renderAnalysisCard(name, result, 'tier1_plausible')
            )}
          </div>
        </div>
      )}

      {/* Tier 2: Speculative */}
      {(expandedTier === 'all' || expandedTier === 'tier2_speculative') && farOut.tier2_speculative && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Radio className="w-5 h-5 text-purple-400" />
            Tier 2: Speculative
          </h4>
          <div className="grid gap-3">
            {Object.entries(farOut.tier2_speculative).map(([name, result]) => 
              renderAnalysisCard(name, result, 'tier2_speculative')
            )}
          </div>
        </div>
      )}

      {/* Tier 3: Wild */}
      {(expandedTier === 'all' || expandedTier === 'tier3_wild') && farOut.tier3_wild && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Brain className="w-5 h-5 text-pink-400" />
            Tier 3: Wild Speculation
          </h4>
          <div className="grid gap-3">
            {Object.entries(farOut.tier3_wild).map(([name, result]) => 
              renderAnalysisCard(name, result, 'tier3_wild')
            )}
          </div>
        </div>
      )}

      {/* Tier 4: Bonkers */}
      {(expandedTier === 'all' || expandedTier === 'tier4_bonkers') && farOut.tier4_bonkers && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Skull className="w-5 h-5 text-red-400" />
            Tier 4: Completely Bonkers
          </h4>
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 mb-3">
            <p className="text-xs text-red-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              These tests range from "extremely speculative" to "are we in The Matrix?"
            </p>
          </div>
          <div className="grid gap-3">
            {Object.entries(farOut.tier4_bonkers).map(([name, result]) => 
              renderAnalysisCard(name, result, 'tier4_bonkers')
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FarOutDiscoveries;

